// src/mobile_pages/const/Photo.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Camera,
  CheckSquare,
  X,
  Calendar,
  Image as ImageIcon
} from "lucide-react";
import { getPhotoList, createPhotoBoard } from "../../api/const";
import Photo_new from "./Photo_new";

export default function Photo() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  // 데이터 상태
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  // 검색
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [total, setTotal] = useState(0);

  // 선택 모드
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // 데이터 가져오기
  const fetchPhotos = async () => {
    if (!projectId) return;
    setLoading(true);

    try {
      const params = {
        project: projectId,
        [searchField]: q,
        page,
        page_size: pageSize,
      };

      const res = await getPhotoList(projectId, params);
      setPhotos(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch (err) {
      console.error("사진 목록 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchPhotos();
  };

  useEffect(() => {
    fetchPhotos();
  }, [projectId, page, pageSize]);

  // 선택 toggle
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // 사진대지 생성
  const handleCreateBoard = async () => {
    if (selectedIds.length === 0) return alert("사진을 선택해주세요.");

    try {
      if (!window.confirm(`${selectedIds.length}장으로 사진대지를 생성하시겠습니까?`)) return;

      const newBoard = await createPhotoBoard({
        project: projectId,
        title: `사진대지 (${new Date().toLocaleDateString()})`,
        photo_ids: selectedIds,
        grid_x: 2,
        grid_y: Math.ceil(selectedIds.length / 2),
        date: new Date().toISOString().split("T")[0],
      });

      alert("생성 완료!");
      navigate(`/project/${projectId}/const/photoboard/view/${newBoard.id}`);
      setSelectMode(false);
      setSelectedIds([]);
    } catch (err) {
      console.error("사진대지 생성 실패:", err);
      alert("생성에 실패했습니다.");
    }
  };

  const handlePhotoClick = (photo) => {
    if (selectMode) {
      toggleSelect(photo.id);
    } else {
      navigate(`view/${photo.id}`);
    }
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(total / pageSize) || 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-8">
        {/* 헤더 */}
        <header className="mb-4">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">
              시공사진
            </h1>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!selectMode && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
                >
                  <Plus size={16} strokeWidth={2.5} />
                  등록
                </button>
              )}
              <button
                onClick={() => {
                  setSelectMode(!selectMode);
                  setSelectedIds([]);
                }}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold shadow-sm active:scale-[0.97] transition-all ${selectMode
                    ? "bg-slate-200 text-slate-700"
                    : "bg-blue-500 text-white"
                  }`}
              >
                {selectMode ? <X size={16} /> : <CheckSquare size={16} />}
                {selectMode ? "취소" : "선택"}
              </button>
            </div>
          </div>
          <p className="mt-1.5 text-[13px] text-slate-500">
            총 {total}장의 시공사진이 등록되어 있습니다
          </p>
        </header>

        {/* 검색 영역 */}
        <section className="mb-4">
          <div className="flex items-center gap-1.5 rounded-xl border bg-white px-2 py-2 shadow-sm min-w-0">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="flex-shrink-0 bg-transparent text-[11px] text-slate-600 font-medium outline-none border-r border-slate-200 pr-1"
            >
              <option value="title">제목</option>
              <option value="author">작성자</option>
            </select>
            <Search size={14} className="text-slate-400 flex-shrink-0" />
            <input
              className="flex-1 min-w-0 bg-transparent text-[13px] text-slate-800 placeholder-slate-400 outline-none"
              placeholder="검색어 입력"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button
              onClick={handleSearch}
              className="flex-shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 active:scale-95"
            >
              검색
            </button>
          </div>
        </section>

        {/* 갤러리 */}
        <section className="mb-4">
          {loading ? (
            <p className="py-12 text-center text-[15px] text-slate-400">
              불러오는 중...
            </p>
          ) : photos.length === 0 ? (
            <div className="py-12 text-center">
              <Camera size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-[15px] text-slate-400">등록된 시공사진이 없습니다</p>
              <p className="text-xs text-slate-400 mt-1">새 사진을 등록해보세요</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {photos.map((photo) => {
                const isSelected = selectedIds.includes(photo.id);
                return (
                  <article
                    key={photo.id}
                    onClick={() => handlePhotoClick(photo)}
                    className={`relative rounded-2xl overflow-hidden border bg-white shadow-sm active:scale-[0.98] transition-all cursor-pointer ${isSelected
                        ? "ring-2 ring-blue-500 border-blue-500"
                        : "border-slate-200"
                      }`}
                  >
                    {/* 이미지 */}
                    <div className="aspect-square bg-slate-100">
                      <img
                        src={photo.file_url}
                        alt={photo.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    {/* 캡션 그라데이션 */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-3 pt-8">
                      <p className="text-white text-[13px] font-medium truncate">
                        {photo.title || "(제목 없음)"}
                      </p>
                      <p className="text-white/70 text-[11px] flex items-center gap-1 mt-0.5">
                        <Calendar size={10} />
                        {photo.date ? new Date(photo.date).toLocaleDateString("ko-KR") : "-"}
                      </p>
                    </div>

                    {/* 선택 체크박스 */}
                    {selectMode && (
                      <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                          ? "bg-blue-500 border-blue-500 scale-110"
                          : "bg-white/90 border-slate-300"
                        }`}>
                        {isSelected && <CheckSquare size={14} className="text-white" />}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <nav className="mt-6 flex items-center justify-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => p - 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canPrev
                  ? "border-slate-300 text-slate-600 active:scale-95"
                  : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors ${page === pageNum
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 active:bg-slate-100"
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canNext
                  ? "border-slate-300 text-slate-600 active:scale-95"
                  : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronRight size={18} />
            </button>
          </nav>
        )}

        {/* 하단 플로팅 액션 (선택 모드일 때만) */}
        {selectMode && selectedIds.length > 0 && (
          <div className="fixed bottom-6 left-4 right-4 z-30">
            <button
              onClick={handleCreateBoard}
              className="w-full bg-slate-900 text-white font-semibold py-4 rounded-2xl shadow-lg flex items-center justify-center gap-3 active:scale-[0.98] transition-transform"
            >
              <div className="bg-white/20 px-2.5 py-1 rounded-full text-xs font-bold">
                {selectedIds.length}장
              </div>
              <span>사진대지 생성하기</span>
            </button>
          </div>
        )}
      </main>

      {/* 사진 등록 모달 */}
      <Photo_new
        projectId={projectId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={fetchPhotos}
      />
    </div>
  );
}
